import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Lock, LogOut, Mail, Phone, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import Seo from "../components/Seo";
import SiteStats from "../components/SiteStats";
import CommercialDashboard from "../components/admin/CommercialDashboard";
import ContactsPanel from "../components/admin/ContactsPanel";
import AgendaPanel from "../components/admin/AgendaPanel";
import AuditLogPanel from "../components/admin/AuditLogPanel";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success(`Bentornato, ${data.name}`);
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-24" data-testid="admin-login-screen">
      <form onSubmit={submit} className="w-full max-w-[420px] border border-[#c9a45c]/30 bg-[#0d0d0d] p-9" data-testid="admin-login-form">
        <Logo />
        <p className="eyebrow mt-9">Area riservata</p>
        <h1 className="display mt-4 text-3xl" data-testid="admin-login-title">Pannello di direzione.</h1>
        <p className="mt-3 text-[13px] leading-relaxed text-[#929292]">
          Accesso riservato alla direzione A.C. HUB per la gestione delle richieste ricevute.
        </p>
        <div className="mt-8 grid gap-4">
          <input
            className="field"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="admin-email-input"
          />
          <input
            className="field"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="admin-password-input"
          />
          <button type="submit" data-testid="admin-login-button" disabled={busy} className="btn btn-gold w-full disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
            {busy ? "Accesso…" : "Accedi"}
          </button>
        </div>
        <Link to="/" data-testid="admin-back-home" className="mt-7 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#929292] transition-colors hover:text-gold-light">
          <ArrowLeft size={12} /> Torna al sito
        </Link>
      </form>
    </div>
  );
}

const PARTNER_STATUSES = ["nuovo", "revisione", "accettato", "rifiutato"];
const PARTNER_STATUS_LABELS = {
  nuovo: "Nuovo",
  revisione: "In revisione",
  accettato: "Accettato",
  rifiuto: "Rifiutato",
  rifiutato: "Rifiutato",
};
const PROFESSION_LABELS = {
  tecnico: "Tecnico / Geometra",
  architetto: "Architetto / Progettista",
  impresa: "Impresa edile",
  finanziario: "Consulente finanziario / Mutui",
  legale: "Notaio / Studio legale",
  altro: "Altro",
};

function PartnerRow({ partner, onStatus, onDelete }) {
  const [status, setStatus] = useState(partner.status);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="border border-[#c9a45c]/18 bg-[#101010] p-5" data-testid={`partner-row-${partner.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif text-lg text-cream" data-testid={`partner-name-${partner.id}`}>{partner.name}</p>
          <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-[#929292]">{fmtDate(partner.created_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="field !w-auto !py-2 !text-[11px]"
            value={status}
            onChange={(e) => {
              const v = e.target.value;
              setStatus(v);
              onStatus(partner.id, v);
            }}
            data-testid={`partner-status-select-${partner.id}`}
          >
            {PARTNER_STATUSES.map((s) => (
              <option key={s} value={s}>{PARTNER_STATUS_LABELS[s]}</option>
            ))}
          </select>
          {confirmDelete ? (
            <span className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#e07a5f]">Eliminare definitivamente?</span>
              <button
                onClick={() => onDelete(partner.id)}
                className="border border-[#e07a5f]/50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#e07a5f]"
                data-testid={`partner-delete-confirm-${partner.id}`}
              >
                Sì, elimina
              </button>
              <button onClick={() => setConfirmDelete(false)} className="text-[10px] uppercase tracking-[0.12em] text-[#929292]" data-testid={`partner-delete-no-${partner.id}`}>
                No
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] hover:text-[#e07a5f]"
              data-testid={`partner-delete-${partner.id}`}
            >
              <Trash2 size={12} /> Elimina
            </button>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#c9c6bf]">
        <a href={`tel:${partner.phone}`} data-testid={`partner-phone-${partner.id}`} className="inline-flex items-center gap-1.5 hover:text-gold-light">
          <Phone size={12} className="text-gold" /> {partner.phone}
        </a>
        {partner.email && (
          <a href={`mailto:${partner.email}`} data-testid={`partner-email-${partner.id}`} className="inline-flex items-center gap-1.5 hover:text-gold-light">
            <Mail size={12} className="text-gold" /> {partner.email}
          </a>
        )}
        <span className="inline-flex items-center gap-1.5 uppercase tracking-[0.1em] text-gold-light" data-testid={`partner-profession-${partner.id}`}>
          {PROFESSION_LABELS[partner.profession] || partner.profession}
        </span>
        {partner.city && <span data-testid={`partner-city-${partner.id}`}>{partner.city}</span>}
        {partner.website && (
          <a href={partner.website.startsWith("http") ? partner.website : `https://${partner.website}`} target="_blank" rel="noopener noreferrer" data-testid={`partner-website-${partner.id}`} className="inline-flex items-center gap-1.5 text-[#8f8d88] underline underline-offset-2 hover:text-gold-light">
            {partner.website}
          </a>
        )}
      </div>
      {partner.message && (
        <p className="mt-3 border-l border-gold/40 pl-4 text-[13px] leading-relaxed text-[#929292]" data-testid={`partner-message-${partner.id}`}>
          {partner.message}
        </p>
      )}
    </div>
  );
}

function PartnerList() {
  const [partners, setPartners] = useState(null);

  useEffect(() => {
    api
      .get("/partners")
      .then(({ data }) => setPartners(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/partners/${id}`, { status });
      setPartners((ps) => ps.map((p) => (p.id === id ? data : p)));
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deletePartner = async (id) => {
    try {
      await api.delete(`/partners/${id}`);
      setPartners((ps) => (ps || []).filter((p) => p.id !== id));
      toast.success("Candidatura eliminata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 grid gap-3 pb-16" data-testid="partner-list">
      {partners === null && (
        <div className="flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[13px] text-[#929292]" data-testid="partner-loading">
          <Loader2 size={14} className="animate-spin text-gold" /> Caricamento richieste partner…
        </div>
      )}
      {partners !== null && partners.length === 0 && (
        <div className="border border-[#c9a45c]/18 bg-[#101010] p-8 text-center text-[13px] text-[#929292]" data-testid="partner-empty">
          Nessuna richiesta di partenariato, per ora.
        </div>
      )}
      {partners !== null && partners.map((p) => (
        <PartnerRow key={p.id} partner={p} onStatus={updateStatus} onDelete={deletePartner} />
      ))}
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("contacts");

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <div className="min-h-screen px-5 py-24 sm:px-8" data-testid="admin-dashboard">
      <Seo
        title="Area Riservata | A.C. HUB"
        description="Pannello di direzione A.C. HUB."
        path="/admin"
        noindex
      />
      <div className="mx-auto max-w-[1080px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Area riservata</p>
            <h1 className="display mt-3 text-3xl sm:text-4xl" data-testid="admin-title">Centro operativo.</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/" data-testid="admin-view-site" className="btn btn-outline !min-h-[42px] !px-4 !text-[9px]">Vedi il sito</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="btn btn-outline !min-h-[42px] !px-4 !text-[9px]">
              <LogOut size={12} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {[
            { v: "contacts", l: "CONTATTI & PIPELINE", t: "admin-tab-contacts" },
            { v: "agenda", l: "AGENDA", t: "admin-tab-agenda" },
            { v: "partners", l: "PARTNER", t: "admin-tab-partners" },
            { v: "stats", l: "STATISTICHE", t: "admin-tab-stats" },
            { v: "audit", l: "REGISTRO", t: "admin-tab-audit" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={t.t}
              onClick={() => setTab(t.v)}
              className={`border px-4 py-3 text-[10px] font-bold tracking-[0.16em] transition-colors duration-200 sm:py-2 ${
                tab === t.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "contacts" ? (
          <div className="mt-6">
            <ContactsPanel />
          </div>
        ) : tab === "agenda" ? (
          <AgendaPanel />
        ) : tab === "partners" ? (
          <PartnerList />
        ) : tab === "audit" ? (
          <AuditLogPanel />
        ) : (
          <div className="mt-6" data-testid="stats-tab">
            <CommercialDashboard />
            <SiteStats />
          </div>
        )}
      </div>
    </div>
  );
}


export default function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-checking">
        <Loader2 size={22} className="animate-spin text-gold" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={() => setUser(null)} /> : <LoginScreen onLogin={setUser} />;
}
